/**
 * Downloads a file from the given URL in a browser context using the Fetch API, a Blob, and a temporary anchor element.[web:5][web:19]
 * Throws an error if the HTTP response is not successful before creating the download link.[web:2]
 * Uses an object URL that is revoked after the synthetic click to avoid leaking browser memory.[web:7][web:15]
 *
 * @param url Absolute or relative URL of the file to download.[web:19]
 * @param filename File name suggested to the browser download dialog.[web:5]
 * @returns A promise that resolves once the download has been triggered.[web:18]
 *
 * @example
 * ```
 * async function exportReport(): Promise<void> {
 *   await downloadFileFromUrl(
 *     'https://example.com/assets/report.pdf',
 *     'quarterly-report.pdf'
 *   );
 * }
 * ```
 */
export async function downloadFileFromUrl(url: string, filename: string): Promise<void> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch: ${response.statusText}`);
  }

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  try {
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
  } finally {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(objectUrl);
  }
}
